import { useEffect } from 'react';

import { useAuthStore } from '@/features/auth/stores/auth-store';

/**
 * BR-14: bound an authenticated session by an inactivity timeout. After
 * `timeoutMs` of no user activity the session is cleared via `logout()`; the
 * route guard (`ProtectedRoute`) then redirects to `/login` with a `redirect`
 * param, so the user returns where they were after signing back in (AC-15).
 *
 * Per-tab timer. Mount once inside the authenticated subtree.
 */
const DEFAULT_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

/** User-activity signals that count as "still active" and reset the timer. */
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'] as const;

export function useInactivityTimeout(timeoutMs: number = DEFAULT_TIMEOUT_MS): void {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const logout = useAuthStore((state) => state.logout);

  useEffect(() => {
    if (!isAuthenticated) return;

    let timer: ReturnType<typeof setTimeout>;

    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(logout, timeoutMs);
    };

    reset();
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, reset, { passive: true }));

    return () => {
      clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, reset));
    };
  }, [isAuthenticated, logout, timeoutMs]);
}
